import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  AdminType,
  SiteType,
  AdminThemeConfig,
  AdminServiceThemeConfig,
  AdminColorPalette,
} from '@app-types/admin-theme';
import { getAdminTheme } from './configs';

/**
 * Admin Theme Context Type
 */
interface AdminThemeContextType {
  // Current state
  adminType: AdminType;
  siteType: SiteType | null;

  // Theme configurations (single theme, no service type distinction)
  themeConfig: AdminThemeConfig;
  theme: AdminServiceThemeConfig;
  colors: AdminColorPalette;
}

/**
 * Admin Theme Context
 */
const AdminThemeContext = createContext<AdminThemeContextType | null>(null);

/**
 * Admin Theme Provider Props
 */
interface AdminThemeProviderProps {
  children: ReactNode;
  adminType: AdminType;
  siteType?: SiteType;
}

/**
 * Admin Theme Provider Component
 */
export const AdminThemeProvider: React.FC<AdminThemeProviderProps> = ({
  children,
  adminType,
  siteType: propSiteType,
}) => {
  // Get site type from URL params or props
  const { site: urlSiteType } = useParams<{ site?: string }>();
  const siteType = propSiteType || urlSiteType || null;

  // Single theme per admin/site (no service type A2/R2)
  const themeConfig = useMemo(
    () => getAdminTheme(adminType, siteType || undefined),
    [adminType, siteType]
  );

  const theme = useMemo(() => themeConfig.adminTheme, [themeConfig]);
  const colors = theme.colors;

  const value: AdminThemeContextType = {
    adminType,
    siteType,
    themeConfig,
    theme,
    colors,
  };

  return <AdminThemeContext.Provider value={value}>{children}</AdminThemeContext.Provider>;
};

/**
 * Hook to use admin theme context (throws when outside provider)
 */
export const useAdminTheme = (): AdminThemeContextType => {
  const context = useContext(AdminThemeContext);
  if (!context) {
    throw new Error('useAdminTheme must be used within an AdminThemeProvider');
  }
  return context;
};

/**
 * Optional admin theme – returns null when not inside AdminThemeProvider.
 * Use in ThemeProvider to delegate to admin theme on admin routes.
 */
export const useOptionalAdminTheme = (): AdminThemeContextType | null => {
  return useContext(AdminThemeContext);
};

// Re-export types
export type { AdminType, SiteType, AdminThemeConfig, AdminServiceThemeConfig, AdminColorPalette };
